/**
 * @module experince/product-selector
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

// tslint:disable max-file-line-count

// core modules
import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  Input,
  EventEmitter,
  Output,
} from '@angular/core';
import { Subject } from 'rxjs';

// services
import { ProductSelectorService } from '@experience/shop/product-selector/services/product-selector.service';
import { EligibleServices, Services } from '@support/api/proxies/responses/api-2102.interface';

// interfaces
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { CartService } from '@support/api/common/retrieveServices.interface';
import {
  LineDeivceDetails,
} from '@experience/shop/product-selector/interfaces/product-selector.interface';
import { TileManagementService } from '@support/shared/services/tile-management.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { ActivatedRoute, Router } from '@angular/router';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { APIError } from '@support/api/common/error.interface';
import { ObjectInterface } from '@support/api/common/api-response.interface';
import {
  PRODUCT_SELECTOR_CONSTANTS,
  PRODUCT_SELECTOR_ROUTES,
} from '@experience/shop/product-selector/constants/product-selector.constants';
import { SHARED_BROWSE_CONSTANTS } from '@experience/shop/shared-browse/constants/shared-browse.constants';
import {
  BladeEntries,
  CompleteServicesInterface,
  ConflictsAndWarnings,
  ServiceBladeInterface,
  ServiceCategory,
} from '@experience/shop/shared-browse/interfaces/service-blade.interface';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import {
  CartItem,
  Cart,
  CartLine,
  ServiceWarning,
} from '@support/api/proxies/responses/api-cart-response.interface';
import { mergeMap, map, tap, takeUntil } from 'rxjs/operators';
import { DynamicModal } from '@support/shared/widgets/interfaces/dynamic-modal.interface';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import { PdpDataHandlerService } from '@root/shared/services/pdp-data-handler.service';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import {
  BUTTON_CONSTANTS,
  CART_EVENT_NAME,
  PLAN_FAMILY,
} from '@support/shared/constants/pdl-data.constants';
import { ProductInfo } from '@support/shared/interfaces/pdl-data.interface';
import { FeatureToggleUtilService } from '@support/shared/services/feature-toggle-util.service';

@Component({
  selector: 'tmo-service-section',
  templateUrl: './service-section.component.html',
  styleUrls: [
    '../../../../../../style-sheets/components/page-titles.component.scss',
    '../../../../../../style-sheets/components/dropdown.component.scss',
    '../../../../../../style-sheets/components/input-radio.component.scss',
    '../../../../../../style-sheets/components/input-checkbox.component.scss',
    '../../../../../../style-sheets/components/html-accordion.component.scss',
    '../../../../../../style-sheets/components/gallery.component.scss',
    '../../../../../../style-sheets/components/service-section.component.scss',
  ],
})
export class ServiceSectionComponent extends PDLBaseComponent implements OnInit, OnDestroy {
  public price: number;
  public showSpinner: boolean;
  @Input() public fromModal: boolean = false;
  @Input() public softGoodsAEMContent: ConfigData;
  /**
   * no plan needed flag to disable service blade
   */
  @Input() public disableServiceBlade: boolean;
  /**
   * Boolean flag to identify if it is a isPrePurchase flow
   */
  @Input() public isPrePurchasedPlan: boolean;

  @ViewChild('accordionTabServices', { static: false }) public accordionTabServices: ElementRef;
  /**
   *  subscription - to track all subscriptions and destory on end of lifecycle
   */
  private ngUnsubscribe: Subject<void>;
  /**
   * Holds list of eligible services and services selected in browse services page
   */
  public eligibleServices: CartService[];
  /**
   * Object to contain services which are in error state
   */
  public cartServiceError: CartItem;

  /**
   * Holds the product selector constants
   */
  public serviceSectionConstants: ObjectInterface;
  /**
   * Holds selected plan
   */
  public perviousSelectedPlan: string;
  /**
   * Holds bundle id
   */
  public bundleId: string;
  /**
   * stores error message on api failure
   */
  public errorMessage: APIError[];
  /**
   * Array to store hardConflict notifications
   */
  public hardConflictNotifications: string[];
  public softConflictNotification: string[];

  /**
   * Stores the error boolean value
   */
  public error: boolean;
  /**
   * Holds Focused tile id details.
   */
  public focusedTileIdDetails: LineDeivceDetails;
  /**
   * Flag to check if assisted flow
   */
  // public isAssistedFlow: boolean;
  /**
   * Input Flag to check if currently the tile is loaded for upgrade flow
   */
  @Input() public isUpgrade: boolean;
  /**
   *
   * Emits boolean value when service api response is success
   */
  @Output() public serviceSuccess: EventEmitter<boolean>;

  @Output() public closeServiceModal: EventEmitter<boolean>;

  public noCompatableServices: boolean;

  public modalDescription: ServiceWarning[];

  public serviceWarningModal: DynamicModal;
  /**
   * Flag for web check
   */
  // public isWebChannel: boolean;
  /**
   * Hold pdl related details to trigger event
   */
  public pdlData: object;
  public isNewProductSelector: boolean;
  public skipZeroDollarService: boolean;
  /**
   * Hold selected/unselected service info to trigger event for PDL
   */
  private selectedServiceInfo: ProductInfo[];
  public isServiceBlockEnable: boolean;
  public connectPlanFamily: string;
  public tabsTitle: string[];
  public isAllZeroDollarService: boolean;
  public skipEnable: boolean;
  public isServiceEditable: boolean;
  public activeTabIndex: number;
  constructor(
    private productSelectorService: ProductSelectorService,
    private commonUtilService: CommonUtilService,
    private cartSupportService: CartSupportService,
    private tileManagementService: TileManagementService,
    private router: Router,
    private stateStoreUtilService: StateStoreUtilsService,
    private objectService: ObjectService,
    public pdlService: PdlService,
    public activatedRoute: ActivatedRoute,
    private pdpDataHandlerService: PdpDataHandlerService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private featureToggleUtilService: FeatureToggleUtilService
  ) {
    super(pdlService, activatedRoute);
    this.ngUnsubscribe = new Subject<void>();
    this.hardConflictNotifications = [];
    this.softConflictNotification = [];
    this.perviousSelectedPlan = '';
    this.skipEnable = false;
    this.error = this.noCompatableServices = false;
    this.connectPlanFamily = PLAN_FAMILY.CONNECTPLAN;
    this.isAllZeroDollarService = false;
    this.serviceWarningModal = {
      disableDismiss: false,
      disableEsc: true,
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: true,
      modalName: 'service-warning-modal',
    };
    this.serviceSuccess = new EventEmitter<boolean>();
    this.closeServiceModal = new EventEmitter<boolean>();
    // this.isWebChannel = SETTINGS.CHANNEL.toLowerCase() ===  CHANNELS.WEB;
  }

  public ngOnInit(): void {
    this.isNewProductSelector =
      this.featureToggleUtilService.isNewProductSelectorTemplateEnabled() &&
      !this.router.url.includes('activate');
    this.skipZeroDollarService = this.featureToggleUtilService.isZeroDollarServiceEnabled();
    this.serviceSectionConstants = PRODUCT_SELECTOR_CONSTANTS;
    this.isServiceEditable =
      !!this.activatedRoute.snapshot.queryParams.isServiceEditable || this.fromModal;
    this.getFocusedTileIdDetails();
    // this.isAssistedFlow = (SETTINGS.CHANNEL.toLowerCase() === CHANNELS.RETAIL ||
    //   SETTINGS.CHANNEL.toLowerCase() === CHANNELS.CARE);
    this.focusedTileIdDetails = this.tileManagementService.getFocusedTileIdDetails();
    if (this.isPrePurchasedPlan) {
      this.getCompatibleServices();
    }
    this.getServiceBlockEnableStatus();
    if (this.isNewProductSelector) {
      this.tileManagementService.getUpdatedData();
    }
    this.isAllZeroDollarService = this.stateStoreUtilService.getServiceZeroDollarFlag();
  }
  /**
   * Destroys the subscriptions
   * @returns void
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.stateStoreUtilService.setFocusedTileLineNonLineItem(this.focusedTileIdDetails);
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Method to get the Services from API 2102
   * @returns void
   */
  private getCompatibleServices(): void {
    if (this.objectService.get(this.focusedTileIdDetails, 'services.services.length', 0) > 0) {
      this.bundleId = this.objectService.get(this.focusedTileIdDetails, 'services.bundleId');
      this.eligibleServices = this.objectService.get(
        this.focusedTileIdDetails,
        'services.services'
      );
      if (this.isNewProductSelector) {
        this.getTabsNames();
      }
      this.setConflictNotifications(this.eligibleServices);
      return null;
    }
    this.showSpinner = true;
    this.productSelectorService
      .getEligibleServices(this.isNewProductSelector)
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(
        (services: CompleteServicesInterface | Services) => {
          this.showSpinner = this.error = false;
          this.bundleId = this.objectService.get(services, 'id');
          if (this.isNewProductSelector) {
            this.changeStructure(<CompleteServicesInterface>services);
          } else {
            this.eligibleServices = this.objectService.get(services, 'details', []);
            this.setConflictNotifications(this.eligibleServices);
          }
          this.serviceSuccess.emit(true);
        },
        (error: APIError[]) => {
          this.errorMessage = error;
          this.noCompatableServices = this.error = true;
          this.showSpinner = false;
          this.serviceSuccess.emit(false);
        }
      );
  }
  /**
   * Method called when a service is checked/unchecked
   * @param {string} selectedServiceId
   */
  public handleClickEvent(selectedServiceId: string): void {
    this.showSpinner = true;
    const bundleId =
      this.objectService.get(this.focusedTileIdDetails, 'services.bundleId') || this.bundleId;
    const selectedItem = this.eligibleServices.filter((item: CartService) => {
      return item.id === selectedServiceId;
    });
    // if (this.objectService.get(selectedItem, '0.selected') && this.isWebChannel) {
    if (this.objectService.get(selectedItem, '0.selected')) {
      this.selectedServiceInfo = this.pdpDataHandlerService.getSelectedServiceInfo(
        this.objectService.get(selectedItem, '0.selected'),
        selectedServiceId
      );
    }
    this.productSelectorService
      .postEligibleServices(bundleId, selectedServiceId, this.isUpgrade, this.isNewProductSelector)
      .pipe(
        takeUntil(this.ngUnsubscribe.asObservable()),
        map((services: EligibleServices | CompleteServicesInterface) => {
          this.isNewProductSelector
            ? this.createServicesData(<CompleteServicesInterface>services)
            : this.getSelectedServices(this.objectService.get(services, 'details'));
        }),
        mergeMap(() =>
          this.cartSupportService.postServiceBundle(
            bundleId,
            !this.isUpgrade
              ? this.focusedTileIdDetails.tileId
              : this.objectService.get(this.focusedTileIdDetails, 'plansFromCart.id')
          )
        )
      )
      .subscribe(
        (cartData: Cart) => {
          this.showSpinner = false;
          this.checkIfServiceInErrorState(selectedServiceId, bundleId);
          const servicesWithWarning = [];
          if (
            cartData &&
            cartData.responseStatus &&
            cartData.responseStatus.code === SHARED_BROWSE_CONSTANTS.ERROR_100
          ) {
            cartData.lines.forEach((line: CartLine) => {
              const serviceObj = {
                lineID: line.id,
                warnings: [],
              };
              line.services.forEach((service: CartItem) => {
                if (
                  service.warningDescription &&
                  service.cartItemStatus === SHARED_BROWSE_CONSTANTS.WARNING
                ) {
                  serviceObj.warnings.push({
                    warningStatusCode: service.warningStatusCode ? service.warningStatusCode : '',
                    warningDescription: service.warningDescription,
                  });
                }
              });
              if (serviceObj.warnings.length > 0) {
                servicesWithWarning.push(serviceObj);
              }
            });

            if (servicesWithWarning.length > 0) {
              this.modalDescription = servicesWithWarning;
              this.serviceWarningModal.toggleModalEmitter.emit({ open: true, close: false });
              // if (this.isWebChannel) {
              this.serviceWarningModal.modalName = this.objectService.get(
                this.softGoodsAEMContent,
                'warningMessage',
                ''
              );
              this.pdlService.triggerCustomEvent(this.pdlPageComponentId, 'modalLoad');
              // }
            }
          }

          // if (this.isWebChannel) {
          if (this.objectService.get(selectedItem, '0.selected')) {
            this.selectedServiceInfo = this.pdpDataHandlerService.getSelectedServiceInfo(
              this.objectService.get(selectedItem, '0.selected'),
              selectedServiceId
            );
          }
          selectedItem[0].selected
            ? this.triggerCartEvent(BUTTON_CONSTANTS.SELECT, CART_EVENT_NAME.CARTADD)
            : this.triggerCartEvent(BUTTON_CONSTANTS.UNSELECT, CART_EVENT_NAME.CARTREMOVE);
          // }
        },
        (error: APIError[]) => {
          this.showSpinner = false;
          this.error = true;
          this.errorMessage = error;
          this.productSelectorService
            .postEligibleServices(bundleId, selectedServiceId)
            .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
            .subscribe((services: EligibleServices | CompleteServicesInterface) => {
              if (this.isNewProductSelector) {
                this.createServicesData(<CompleteServicesInterface>services);
              } else {
                this.getSelectedServices(this.objectService.get(services, 'details'));
              }
              this.showSpinner = false;
            });
        }
      );
  }

  /**
   * Checks if service is in error state, if error state, call 2113 to deselect the service
   * @param selectedServiceId string
   * @param bundleId string
   * @return void
   */
  public checkIfServiceInErrorState(selectedServiceId: string, bundleId: string): void {
    const servicesInFocus = this.cartSupportService.checkIfServiceInErrorState();
    this.cartServiceError = null;
    if (this.objectService.get(servicesInFocus, '0')) {
      this.cartServiceError = this.objectService.get(servicesInFocus, '0');
      this.productSelectorService
        .postEligibleServices(
          bundleId,
          this.cartServiceError.offerId,
          false,
          this.isNewProductSelector
        )
        .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
        .subscribe((services: EligibleServices | CompleteServicesInterface) => {
          if (this.isNewProductSelector) {
            this.createServicesData(<CompleteServicesInterface>services);
          } else {
            this.getSelectedServices(this.objectService.get(services, 'details'));
          }
          this.showSpinner = false;
        });
    }
  }

  /**
   * Function used to push selected services
   * @param {CartService[]} services
   */
  private getSelectedServices(services: CartService[]): void {
    if (this.objectService.get(services, 'length') > 0) {
      this.eligibleServices = [];
      for (const service of services) {
        this.eligibleServices.push(service);
      }
      this.setConflictNotifications(this.eligibleServices);
      this.focusedTileIdDetails.services = {
        services: this.eligibleServices,
        bundleId: this.bundleId,
      };
    }
  }

  /**
   * Function used to push selected services
   * @param {CartService[]} services
   */
  private createServicesData(services: CompleteServicesInterface): void {
    this.changeStructure(services);
    this.focusedTileIdDetails.services = {
      services: this.eligibleServices,
      bundleId: this.bundleId,
    };
  }
  /**
   * Navigate to BrowseService
   */
  public browseServiceNavigate(): void {
    this.router.navigate([PRODUCT_SELECTOR_CONSTANTS.NAV_BROWSE_SERVICES]);
  }

  /**
   * Function used to get focused tile id details
   */
  private getFocusedTileIdDetails(): void {
    this.tileManagementService.focusedTileDetails
      .pipe(
        takeUntil(this.ngUnsubscribe.asObservable()),
        tap((newLineDetails: LineDeivceDetails) => {
          this.setServicesInCache(newLineDetails); // Caching services
        })
      )
      .subscribe((data: LineDeivceDetails) => {
        // @TODO check if this can be removed by rxjs operators
        this.perviousSelectedPlan = this.objectService.get(
          this.focusedTileIdDetails,
          'plansFromCart.offerCode'
        );
        this.focusedTileIdDetails = data;
        this.checkIfCompatibleServicesCallNeeded();
        this.getServiceBlockEnableStatus();
      });
  }

  /**
   * Function stores services in cache
   * @param {LineDeivceDetails} newLineDetails
   */
  private setServicesInCache(newLineDetails: LineDeivceDetails): void {
    if (
      this.objectService.get(newLineDetails, 'tileId') !==
        this.objectService.get(this.focusedTileIdDetails, 'tileId') &&
      this.objectService.get(this.eligibleServices, 'length') > 0
    ) {
      this.focusedTileIdDetails.services = {
        services: this.eligibleServices,
        bundleId: this.bundleId,
      };
      this.stateStoreUtilService.setFocusedTileLineNonLineItem(
        this.focusedTileIdDetails,
        this.objectService.get(this.focusedTileIdDetails, 'tileId')
      );
      this.bundleId = this.errorMessage = this.error = this.eligibleServices = null;
      this.perviousSelectedPlan = null;
      this.hardConflictNotifications = [];
      this.softConflictNotification = [];
    }
  }
  /**
   * Method to set conflicting Services name
   * @returns void
   */
  private setConflictNotifications(eligibleServices: CartService[]): void {
    this.hardConflictNotifications = [];
    this.softConflictNotification = [];
    for (const service of eligibleServices) {
      this.setConflictNotificationsforSpecificService(service);
    }
  }
  /**
   * Method to set conflicting Services for specific services
   * @returns void
   */
  private setConflictNotificationsforSpecificService(service: CartService): void {
    let hardConflictNotificationMessage: string;
    let softConflictNotificationMessage: string;
    const hardConflicts = service.conflictsAndWarnings.filter(
      (conflict: ConflictsAndWarnings) => conflict.type === SHARED_BROWSE_CONSTANTS.HARDCONFLICT
    );
    const softConflicts = service.conflictsAndWarnings.filter(
      (conflict: ConflictsAndWarnings) => conflict.type === SHARED_BROWSE_CONSTANTS.SOFTCONFLICT
    );
    if (hardConflicts && hardConflicts.length > 0) {
      const hardConflictsName = hardConflicts
        .map((conflict: ConflictsAndWarnings) => conflict.offerName)
        .join(', ');
      hardConflictNotificationMessage = this.objectService.get(
        this.softGoodsAEMContent,
        'hardConflictNotification'
      )
        ? this.commonUtilService.messageFormatter(
            this.softGoodsAEMContent.hardConflictNotification,
            [hardConflictsName, hardConflictsName, service.offerName]
          )
        : '';
    } else if (softConflicts && softConflicts.length) {
      const softConflictsName = softConflicts
        .map((conflict: ConflictsAndWarnings) => conflict.offerName)
        .join(', ');
      softConflictNotificationMessage = this.objectService.get(
        this.softGoodsAEMContent,
        'softConflictNotification'
      )
        ? this.commonUtilService.messageFormatter(
            this.softGoodsAEMContent.softConflictNotification,
            [softConflictsName]
          )
        : '';
    }
    this.hardConflictNotifications.push(hardConflictNotificationMessage);
    this.softConflictNotification.push(softConflictNotificationMessage);
  }

  /**
   * Function to check if compatible services API call needed
   */
  private checkIfCompatibleServicesCallNeeded(): void {
    const newLineSelectedPlan = this.objectService.get(
      this.focusedTileIdDetails,
      'plansFromCart.offerCode'
    );
    if (
      newLineSelectedPlan &&
      this.perviousSelectedPlan !== newLineSelectedPlan &&
      this.isNewProductSelector
    ) {
      this.objectService.set(this.focusedTileIdDetails, 'services.services', []);
      this.objectService.set(this.focusedTileIdDetails, 'services.bundleId', null);
    }
    if (
      newLineSelectedPlan &&
      (this.perviousSelectedPlan !== newLineSelectedPlan || !this.eligibleServices)
    ) {
      this.getCompatibleServices();
    }
  }

  public closeModal(): void {
    this.serviceWarningModal.toggleModalEmitter.emit({
      open: false,
      close: true,
    });
  }

  /**
   * Method to set data for cart events and trigger cart events (cartRemove, cartAdd)
   * @returns void
   */
  private triggerCartEvent(ctaName?: string, action?: string): void {
    const cart = this.stateStoreUtilsService.getCart();
    this.pdlData = {
      ctaName: ctaName,
      product: this.selectedServiceInfo,
      assetType: 'Checkbox',
    };
    const isCartEmpty = !this.cartSupportService.isCartNotEmpty(cart);
    this.pdpDataHandlerService.triggerCartEvent(cart, this.pdlPageComponentId, isCartEmpty, action);
  }

  public getServiceBlockEnableStatus(): void {
    const isExisting = 2;
    this.isServiceBlockEnable =
      (this.objectService.get(this.focusedTileIdDetails, 'lineTileType') !== 1 &&
        this.objectService.get(this.focusedTileIdDetails, 'plansFromCart.familyCode')) ||
      (this.objectService.get(this.focusedTileIdDetails, 'lineTileType') === isExisting &&
        !this.objectService.get(this.focusedTileIdDetails, 'isUpgrade'));
  }

  /**
   * function gotoCart for Lines
   * @method goToCart()
   * @returns void
   */
  public goToCart(): void {
    if (this.fromModal) {
      this.closeServiceModal.emit(true);
      return;
    }
    let params;
    const tileIDInFocusLineDetails = this.tileManagementService.getFocusedTileIdDetails();
    const isUpgrade = this.objectService.get(tileIDInFocusLineDetails, 'isUpgrade');
    if (isUpgrade) {
      params = { queryParams: { isUpgrade: true } };
      this.router.navigate([PRODUCT_SELECTOR_ROUTES.CART], params);
    } else {
      this.router.navigate([PRODUCT_SELECTOR_ROUTES.CART]);
    }
  }

  /**
   * Method to use for skip service
   * @returns void
   */
  public skipService(event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    const selectedServiceList = this.eligibleServices.filter((item: CartService) => {
      return item.selected;
    });
    if (selectedServiceList.length) {
      this.goToCart();
      // this.handleClickEvent(selectedServiceList[0].id);
    } else {
      this.goToCart();
    }
  }

  private checkServicePricing(): void {
    const data = this.eligibleServices.filter((service: CartService) => {
      return this.objectService.get(service, 'price.monthlyPrice.salePrice.amount') > 0;
    });
    if (
      this.skipZeroDollarService &&
      !this.isServiceEditable &&
      !this.stateStoreUtilService.getServiceZeroDollarFlag() &&
      this.isNewProductSelector &&
      !data.length &&
      this.eligibleServices.length &&
      !this.objectService.get(this.focusedTileIdDetails, 'isUpgrade', false)
    ) {
      this.stateStoreUtilService.setServiceZeroDollarFlag(true);
      this.isAllZeroDollarService = true;
      this.skipService();
    } else {
      this.setConflictNotifications(this.eligibleServices);
      if (data.length) {
        this.stateStoreUtilService.setServiceZeroDollarFlag(false);
        this.isAllZeroDollarService = false;
      }
    }
    this.checkSelectedService();
  }
  /**
   * Method to get dynamic tabs name from focus tile
   * @returns void
   */
  private getTabsNames(): void {
    const familyName = [];
    this.tabsTitle = [];
    this.setActiveTabIndex(this.eligibleServices, true);
    this.eligibleServices.forEach((item: CartService, key: number) => {
      familyName.push(item.familyName);
    });
    this.tabsTitle = [...Array.from(new Set(familyName))];
    this.checkServicePricing();
  }
  public setActiveTabIndex(services: CartService[], removeDuplicates: boolean = false) {
    if (this.activeTabIndex !== null && this.activeTabIndex !== undefined) {
      return false;
    }
    let eligibleServices = JSON.parse(JSON.stringify(services));
    if (removeDuplicates) {
      eligibleServices = [...Array.from(new Set(eligibleServices))];
    }
    const tabIndex = eligibleServices.findIndex(
      (item: CartService | ServiceBladeInterface) =>
        item.familyName === PRODUCT_SELECTOR_CONSTANTS.INTERNATIONAL_FAMILY
    );
    this.activeTabIndex = tabIndex !== -1 ? tabIndex : 0;
  }
  /**
   * Method to crate old data for eligibleServices and tabs from group response
   * @returns void
   */
  private getService(services: CompleteServicesInterface): CartService[] {
    this.tabsTitle = [];
    this.eligibleServices = [];
    const eligibleServices = [];
    services.category.forEach((category: ServiceCategory) => {
      this.setActiveTabIndex(category.family);
      category.family.forEach((family: ServiceBladeInterface, index: number) => {
        this.tabsTitle.push(family.familyName);
        family.details.forEach((detail: BladeEntries) => {
          this.objectService.set(detail, 'familyName', family.familyName);
          eligibleServices.push(<BladeEntries>detail);
        });
      });
    });
    return eligibleServices;
  }

  /**
   * Method to set eligibleServices data and proceed with other function call
   * @returns void
   */
  private changeStructure(services: CompleteServicesInterface): void {
    if (services && services.category && services.category.length) {
      this.eligibleServices = this.getService(services);
    } else {
      this.eligibleServices = [];
    }
    this.checkServicePricing();
  }

  public checkSelectedService(): void {
    const selectedServiceList = this.eligibleServices.filter((item: CartService) => {
      return item.selected;
    });
    this.skipEnable = !!selectedServiceList.length;
  }
}
